import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, EmptyState } from "@/components/Loading";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Star, ClipboardCheck, MessageSquare, CheckCircle2, BarChart3 } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { key: "program_rating", label: "تقييم البرنامج بشكل عام" },
  { key: "content_rating", label: "تقييم المحتوى العلمي" },
  { key: "lecturer_rating", label: "تقييم المحاضر" },
  { key: "organization_rating", label: "تقييم التنظيم" },
  { key: "unit_rating", label: "تقييم الوحدة السكنية" },
  { key: "benefit_rating", label: "مدى الاستفادة" },
];

function Stars({ value, onChange, readOnly }) {
  return (
    <div className="flex gap-1" dir="ltr">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" disabled={readOnly} onClick={() => onChange && onChange(n)}
          data-testid={readOnly ? undefined : `star-${n}`}
          className={`transition-transform ${readOnly ? "" : "hover:scale-110 active:scale-95"}`}>
          <Star className={`h-6 w-6 ${n <= value ? "fill-gold text-gold" : "text-slate-200"}`} />
        </button>
      ))}
    </div>
  );
}

export default function Evaluations() {
  const { user } = useAuth();
  return user.role === "student" ? <StudentEval /> : <AdminEval />;
}

/* -------- Student -------- */
function StudentEval() {
  const [programs, setPrograms] = useState(null);
  const [mine, setMine] = useState([]);
  const [active, setActive] = useState(null);
  const [ratings, setRatings] = useState({});
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const enr = await api.get("/enrollments");
    setPrograms(enr.data.filter((e) => e.status === "approved"));
    const m = await api.get("/evaluations/mine");
    setMine(m.data);
  };
  useEffect(() => { load().catch(() => setPrograms([])); }, []);

  const evaluatedIds = new Set(mine.map((e) => e.program_id));

  const openForm = (e) => {
    setActive(e);
    setRatings(FIELDS.reduce((a, f) => ({ ...a, [f.key]: 5 }), {}));
    setComment("");
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/evaluations", { program_id: active.program_id, ...ratings, comment });
      toast.success("تم إرسال تقييمك، شكرًا لك");
      setActive(null);
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (programs === null) return <PageLoader />;

  return (
    <>
      <PageHeader title="تقييم البرامج" subtitle="شاركنا رأيك في البرامج والمحاضرين لتحسين الجودة" />
      {programs.length === 0 ? <EmptyState icon={ClipboardCheck} title="لا توجد برامج لتقييمها" desc="سجّل في برنامج وانتظر القبول لتتمكن من تقييمه." /> : (
        <div className="stagger grid gap-4">
          {programs.map((e) => {
            const done = evaluatedIds.has(e.program_id);
            const finished = e.program?.status === "finished";
            return (
              <div key={e.id} data-testid={`eval-prog-${e.program_id}`} className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-slate-100 bg-white p-5 soft-shadow">
                <div>
                  <p className="font-heading font-bold text-navy">{e.program_name}</p>
                  <p className="mt-1 text-xs text-slate-400">{finished ? "البرنامج منتهٍ" : "البرنامج قيد التنفيذ"}</p>
                </div>
                {done ? (
                  <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-4 py-2 text-sm font-semibold text-emerald-800"><CheckCircle2 className="h-4 w-4" /> تم التقييم</span>
                ) : (
                  <Button data-testid={`eval-btn-${e.program_id}`} onClick={() => openForm(e)} className="rounded-xl bg-navy hover:bg-navy-light"><Star className="me-2 h-4 w-4" /> تقييم البرنامج</Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-navy">تقييم: {active?.program_name}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {FIELDS.map((f) => (
              <div key={f.key} className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3">
                <span className="text-sm font-semibold text-navy">{f.label}</span>
                <Stars value={ratings[f.key]} onChange={(v) => setRatings((r) => ({ ...r, [f.key]: v }))} />
              </div>
            ))}
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-navy">اقتراحات وملاحظات</label>
              <Textarea data-testid="eval-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={3} className="rounded-lg" placeholder="اكتب ملاحظاتك هنا..." />
            </div>
          </div>
          <DialogFooter><Button data-testid="eval-submit" onClick={submit} disabled={saving} className="rounded-xl bg-greenx hover:bg-greenx-light">{saving ? "جارٍ الإرسال..." : "إرسال التقييم"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* -------- Admin / Teacher -------- */
function AdminEval() {
  const [programs, setPrograms] = useState([]);
  const [programId, setProgramId] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/programs").then((r) => { setPrograms(r.data); if (r.data[0]) setProgramId(r.data[0].id); }).catch(() => {}); }, []);
  useEffect(() => {
    if (!programId) return;
    setData(null);
    api.get(`/evaluations?program_id=${programId}`).then((r) => setData(r.data)).catch(() => setData({ count: 0 }));
  }, [programId]);

  return (
    <>
      <PageHeader title="نتائج تقييم البرامج" subtitle="إحصائيات تقييمات الطلاب للبرامج والمحاضرين"
        action={
          <Select value={programId} onValueChange={setProgramId}>
            <SelectTrigger data-testid="eval-program-select" className="w-56 rounded-xl"><SelectValue placeholder="اختر برنامج" /></SelectTrigger>
            <SelectContent>{programs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        }
      />
      {data === null ? <PageLoader /> : (data.count === 0) ? <EmptyState icon={BarChart3} title="لا توجد تقييمات بعد" desc="ستظهر النتائج بعد أن يقيّم الطلاب البرنامج." /> : (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-slate-100 bg-white p-6 soft-shadow">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold text-navy">متوسط التقييمات</h3>
              <span className="rounded-full bg-gold/10 px-3 py-1 text-xs font-semibold text-gold-dark">{data.count} مشارك</span>
            </div>
            <div className="space-y-4">
              {FIELDS.map((f) => {
                const v = data.averages[f.key] || 0;
                return (
                  <div key={f.key}>
                    <div className="mb-1 flex justify-between text-sm">
                      <span className="text-slate-600">{f.label}</span>
                      <span className="flex items-center gap-1 font-bold text-navy"><Star className="h-3.5 w-3.5 fill-gold text-gold" /> {v}</span>
                    </div>
                    <Progress value={(v / 5) * 100} className="h-2" />
                  </div>
                );
              })}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-100 bg-white p-6 soft-shadow">
            <h3 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-navy"><MessageSquare className="h-5 w-5" /> ملاحظات الطلاب</h3>
            {(!data.comments || data.comments.length === 0) ? <p className="py-6 text-center text-sm text-slate-400">لا توجد ملاحظات مكتوبة</p> : (
              <div className="space-y-3">
                {data.comments.map((c, i) => (
                  <div key={i} className="rounded-xl bg-slate-50 p-4 text-sm leading-relaxed text-slate-600">"{c}"</div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
